import { useAuth } from "@/shared/providers/AuthProvider";
import { useTenantContext } from "@/shared/providers/TenantProvider";
import { FeatureToggleService } from "@/shared/services/featureToggle.service";
import { FeatureToggleSource } from "@/shared/services/features/feature.types";
import type { FeatureToggleResult } from "@/shared/services/features/feature.types";

export const useFeatureToggle = () => {
  const { user } = useAuth();
  const { tenant } = useTenantContext();
  const featureService = FeatureToggleService.getInstance();

  // Check if feature is enabled using full hierarchy (emergency -> global -> tenant -> overrides -> experiments)
  const isFeatureEnabled = (
    featureKey: string,
    options?: {
      userOverrides?: Record<string, boolean>;
    },
  ): FeatureToggleResult => {
    if (!user || !tenant) {
      return {
        enabled: false,
        reason: "User or tenant not loaded",
        source: FeatureToggleSource.Global,
      };
    }

    return featureService.isFeatureEnabled(
      featureKey,
      user,
      tenant,
      options?.userOverrides,
    );
  };

  // Get feature toggle result with full context (enabled, reason, variant, source)
  const getFeatureToggle = (
    featureKey: string,
    options?: {
      userOverrides?: Record<string, boolean>;
    },
  ): FeatureToggleResult => {
    return isFeatureEnabled(featureKey, options);
  };

  // Check if feature is simply enabled (boolean convenience method)
  const isEnabled = (
    featureKey: string,
    userOverrides?: Record<string, boolean>,
  ): boolean => {
    const options = userOverrides ? { userOverrides } : undefined;
    return isFeatureEnabled(featureKey, options).enabled;
  };

  // Get experiment variant if user is in an experiment
  const getExperimentVariant = (experimentKey: string): string | undefined => {
    if (!user || !tenant) return undefined;

    const result = featureService.isFeatureEnabled(experimentKey, user, tenant);
    return result.variant;
  };

  // Check multiple features at once with full results
  const checkFeatures = (
    featureKeys: string[],
    userOverrides?: Record<string, boolean>,
  ): Record<string, FeatureToggleResult> => {
    const results: Record<string, FeatureToggleResult> = {};
    const options = userOverrides ? { userOverrides } : undefined;

    featureKeys.forEach((key) => {
      results[key] = isFeatureEnabled(key, options);
    });

    return results;
  };

  // Get all currently enabled features for debugging
  const getEnabledFeatures = (
    userOverrides?: Record<string, boolean>,
  ): string[] => {
    if (!tenant?.features) return [];

    const enabledFeatures: string[] = [];
    Object.keys(tenant.features).forEach((featureKey) => {
      if (isEnabled(featureKey, userOverrides)) {
        enabledFeatures.push(featureKey);
      }
    });

    return enabledFeatures;
  };

  // Development helper to see feature toggle reasoning
  const debugFeature = (
    featureKey: string,
    userOverrides?: Record<string, boolean>,
  ) => {
    const options = userOverrides ? { userOverrides } : undefined;
    const result = isFeatureEnabled(featureKey, options);
    return result;
  };

  return {
    isFeatureEnabled,
    getFeatureToggle,
    isEnabled,
    getExperimentVariant,
    checkFeatures,
    getEnabledFeatures,
    debugFeature,
  };
};

// Legacy hook for backward compatibility with single feature checking
export const useFeatureFlag = (featureKey: string) => {
  const { isFeatureEnabled, getExperimentVariant } = useFeatureToggle();

  const result = isFeatureEnabled(featureKey);

  return {
    enabled: result.enabled,
    reason: result.reason,
    variant: result.variant,
    source: result.source,
    getVariant: () => getExperimentVariant(featureKey),
  };
};
